
import L, { LeafletMouseEvent, Marker, PointExpression } from 'leaflet';
import { defineComponent, PropType } from "vue";

window.L = L;

import { LMap, LTileLayer } from "@vue-leaflet/vue-leaflet";
import { MarkerClusterGroup } from 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import moment from "moment";


import { Station } from "../../api/models/station.model";
import { AvoltaAlarmConfiguration } from "../../configs/alarms/avolta-alarm.config";
import { AvoltaAlarmEnum, AvoltaAlarmKeyEnum } from "../../configs/alarms/avolta-alarm.model";
import { AvoltaIndicator, indicatorsConfiguration } from '../../configs/indicators/avolta-indicators.config';
import { IndicatorConfiguration } from '../../configs/indicators/avolta-indicators.model';

export default defineComponent({

    components: {
        LMap,
        LTileLayer
    },

    props: {
        stations: { type: Array as PropType<Station[]>, default: () => []},
        // eslint-disable-next-line vue/require-valid-default-prop
        center: { type: Object as PropType<PointExpression>, default: () => [0,0]},
        zoom: { type: Number, default: 1},
    },

    data() {
        return {
            //@ts-ignore
            clusterLayer: new MarkerClusterGroup({ iconCreateFunction: (cluster) => this.getClusterIcon(cluster) })
        }
    },

    computed: {

        map(): L.Map {

            if(!this.$refs.map) {
                return null;
            }

            return (this.$refs.map as typeof LMap).leafletObject as L.Map;
        }
    },

    methods: {

        async onMapReady(): Promise<void> {

            setTimeout(() => {
                this.map.invalidateSize();
            }, 500);

            this.map.attributionControl.setPrefix('');

            //@ts-ignore
            this.clusterLayer = new MarkerClusterGroup({ iconCreateFunction: (cluster) => this.getClusterIcon(cluster) });
            this.map.addLayer(this.clusterLayer);
            await this.$nextTick();

            if(this.map && this.stations && this.stations.length) {
                this.updateClusters(this.stations);
            }

            this.map.on('dblclick', (event: LeafletMouseEvent) => {
                this.$emit('map:dblclick', event.latlng);
            });

        },

        updateClusters(stations: Station[]): void {

            this.clusterLayer.clearLayers();

            if(!stations || !stations.length) {
                return;
            }

            const markers = stations
                .filter(s => s?.location?.lat && s?.location?.lon)
                .map(s => {
                    const marker = this.createStationMarker(s);
                    if(!marker) {
                        return null
                    }
                    
                    return marker;
                })
                .filter(s => s);

            this.clusterLayer.addLayers(markers);
            if(this.map && markers?.length) {
                const featureGroup = new L.FeatureGroup(markers);
                this.map.fitBounds(featureGroup.getBounds(), { padding: [50,50]});
            }
        },

        createStationMarker(station: Station): Marker | null {

            if(!station || !station?.location?.lat || !station?.location.lon) {
                return null;
            }

            const options : L.MarkerOptions | { station: Station, clickable: boolean } = { 
                title: station.name, 
                clickable: true, 
                draggable: false,
                icon: L.icon({ iconUrl: this.getIconUrl(station), iconAnchor: [17.5,53], iconSize: [35,53]}),
                station: station,
             } as L.MarkerOptions;
            const marker = new L.Marker([station?.location?.lat, station?.location?.lon], options);
            const indicator = indicatorsConfiguration.find((i: IndicatorConfiguration) => i.key === AvoltaIndicator.CH4_lel);
            const ch4Lel: number = station?.latest_values[AvoltaIndicator.CH4_lel];

            const alarms = new Set<AvoltaAlarmEnum>();

            if(station.latest_values['CH4_concentration_alarm']) {
                alarms.add(AvoltaAlarmEnum.CH4_concentration_alarm);
            }

            if(station.latest_values['CH4_sensor_error_alarm']) {
                alarms.add(AvoltaAlarmEnum.CH4_system_failure);
            }

            if(station.latest_values['CH4_battery_alarm']) {
                alarms.add(AvoltaAlarmEnum.CH4_battery_alarm);
            }

            if(station.status === 'offline') {
                alarms.add(AvoltaAlarmEnum.offline);
            }

            const alarmsIcon = Array.from(alarms).map(a => `<span class="mr-1 is-size-4 ${AvoltaAlarmConfiguration[a]?.icon}"></span>`).join('');

            marker.bindTooltip(
                L.tooltip({
                    interactive: true,
                    direction: 'top',
                    offset: [0,-53],
                    content: `
                        <div>
                            <span class="is-size-6 has-text-link has-text-weight-semibold">${station.name}</span>
                            <br/>
                            <span class="mdi mdi-history"></span>
                            <span>${station.last_seen ? moment.unix(station.last_seen).fromNow(): '-'}</span>
                            <br/>
                            <span class="has-text-weight-semibold">${indicator.label}:</span>
                            <span>${ch4Lel !== null && ch4Lel!== undefined ? ch4Lel: '-'} ${indicator.unit}</span>
                            <br/>
                            ${alarmsIcon}
                        </div>
                    `,
                })
            );

            marker.on('click', () => {
                this.$router.push({ name: 'station-details', params: { station: station.id}})
            })


            return marker;
        },

        getClusterIcon(cluster): L.DivIcon {
            let html = '';
            try {
                const markers = cluster.getAllChildMarkers();
                const stations : Station[] = markers.map(m => m.options.station);

                const hasMajorError: boolean = stations.some(s => s.latest_values[AvoltaAlarmKeyEnum.CH4_concentration_alarm]);
                const hasMinorError: boolean = stations.some(s => {
                    return s.latest_values[AvoltaAlarmKeyEnum.CH4_battery_alarm] || s.latest_values[AvoltaAlarmKeyEnum.CH4_sensor_error_alarm] || s.status === 'offline'
                });
                

                let tags = '';
                if(hasMajorError) {
                    tags = '<span class="mdi mdi-alert-circle has-text-danger"></span>';
                } else if(hasMinorError) {
                    tags = '<span class="mdi mdi-alert-circle has-text-warning"></span>';
                } else {
                    tags = '<span class="mdi mdi-check-circle has-text-success"></span>';
                }

                html = `
                 <div class="map-cluster-marker center is-clickable">
                     <div class="map-cluster-status">
                        ${tags}
                     </div>
                     ${cluster.getChildCount()}
                 </div>
                `;
                return L.divIcon({ html, className: 'dummy' })
            } catch(error) {
                console.error(error);
                return L.divIcon({ html })
            }
        },

        getIconUrl(station: Station): string {
            
            if(station?.latest_values[AvoltaAlarmKeyEnum.CH4_concentration_alarm]) {
                return require('@/map/map-marker-alert-major-icon.png').default;
            } else if(station?.latest_values[AvoltaAlarmKeyEnum.CH4_battery_alarm]) {
                return require('@/map/map-marker-alert-minor-icon.png').default;
            } else if(station?.latest_values[AvoltaAlarmKeyEnum.CH4_sensor_error_alarm]) {
                return require('@/map/map-marker-alert-minor-icon.png').default;
            } else if(station?.status === 'offline') {
                return require('@/map/map-marker-alert-minor-icon.png').default;
            } else {
                return require('@/map/map-marker-good-icon.png').default;
            }
        }
    },

    watch: {
        stations(stations) {
            this.updateClusters(stations);
        }
    }


})